const DEFAULT_ROOT_DOMAIN = process.env.REACT_APP_TENANT_ROOT_DOMAIN || 'phennx.io';
const DEFAULT_PROTOCOL =
  process.env.REACT_APP_TENANT_PROTOCOL ||
  (typeof window !== 'undefined' && window.location.protocol === 'http:' ? 'http:' : 'https:');
const DEFAULT_API_PREFIX = process.env.REACT_APP_TENANT_API_PREFIX || '/api';
const DEFAULT_LOCAL_APP_PORT = process.env.REACT_APP_TENANT_APP_PORT || '3000';
const DEFAULT_LOCAL_API_PORT = process.env.REACT_APP_TENANT_API_PORT || '8000';
const DEFAULT_COOKIE_DOMAIN = process.env.REACT_APP_TENANT_COOKIE_DOMAIN || `.${DEFAULT_ROOT_DOMAIN}`;
const DEFAULT_LOCAL_COOKIE_DOMAIN = process.env.REACT_APP_TENANT_LOCAL_COOKIE_DOMAIN || '.localhost';
const TOKEN_COOKIE_NAMES = ['token', 'refresh'];
const SESSION_COOKIE_FIELDS = {
  token: ['token', 'access'],
  refresh: ['refresh'],
};

function trimTrailingSlash(value) {
  return value.replace(/\/+$/, '');
}

function replaceOrganizationPlaceholder(template, organizationId) {
  return template.replace(/\{org\}/gi, organizationId);
}

function buildCookieString(name, value, options = {}) {
  const parts = [`${name}=${value}`, 'Path=/', 'SameSite=Lax'];

  if (options.domain) {
    parts.push(`Domain=${options.domain}`);
  }

  if (options.expires) {
    parts.push(`Expires=${options.expires.toUTCString()}`);
  }

  if (options.secure) {
    parts.push('Secure');
  }

  return parts.join('; ');
}

function getCookieDomain(hostname) {
  if (!hostname) {
    return '';
  }

  if (hostname.endsWith(`.${DEFAULT_ROOT_DOMAIN}`) || hostname === DEFAULT_ROOT_DOMAIN) {
    return DEFAULT_COOKIE_DOMAIN;
  }

  if (hostname.endsWith('.localhost') || hostname === 'localhost') {
    return DEFAULT_LOCAL_COOKIE_DOMAIN;
  }

  return '';
}

export function normalizeOrganizationId(value) {
  const rawValue = String(value || '').trim().toLowerCase();

  if (!rawValue) {
    return '';
  }

  let normalized = rawValue
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .split('?')[0]
    .split('#')[0]
    .replace(/:\d+$/, '');

  const productionSuffix = `.${DEFAULT_ROOT_DOMAIN}`;
  if (normalized.endsWith(productionSuffix)) {
    normalized = normalized.slice(0, -productionSuffix.length);
  }

  if (normalized.endsWith('.localhost')) {
    normalized = normalized.slice(0, -'.localhost'.length);
  }

  if (normalized.includes('.')) {
    normalized = normalized.split('.')[0];
  }

  return normalized.replace(/[^a-z0-9-]/g, '');
}

export function resolveTenantTargets(organizationId) {
  const normalizedOrganizationId = normalizeOrganizationId(organizationId);

  if (!normalizedOrganizationId) {
    return null;
  }

  const customAppOriginTemplate = process.env.REACT_APP_TENANT_APP_ORIGIN_TEMPLATE;
  const customApiBaseTemplate = process.env.REACT_APP_TENANT_API_BASE_TEMPLATE;

  let appOrigin = '';
  let apiBaseUrl = '';

  if (customAppOriginTemplate && customApiBaseTemplate) {
    appOrigin = trimTrailingSlash(
      replaceOrganizationPlaceholder(customAppOriginTemplate, normalizedOrganizationId)
    );
    apiBaseUrl = trimTrailingSlash(
      replaceOrganizationPlaceholder(customApiBaseTemplate, normalizedOrganizationId)
    );
  } else if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost'))
  ) {
    appOrigin = `http://${normalizedOrganizationId}.localhost:${DEFAULT_LOCAL_APP_PORT}`;
    apiBaseUrl = `http://${normalizedOrganizationId}.localhost:${DEFAULT_LOCAL_API_PORT}`;
  } else {
    appOrigin = `${DEFAULT_PROTOCOL}//${normalizedOrganizationId}.${DEFAULT_ROOT_DOMAIN}`;
    apiBaseUrl = `${appOrigin}${DEFAULT_API_PREFIX}`;
  }

  const appUrl = new URL(appOrigin);

  return {
    organizationId: normalizedOrganizationId,
    appOrigin,
    apiBaseUrl,
    appHostname: appUrl.hostname,
    originLabel: appUrl.host,
    cookieDomain: getCookieDomain(appUrl.hostname),
  };
}

export function sanitizeRedirectTarget(target) {
  if (!target || !target.startsWith('/') || target.startsWith('//')) {
    return null;
  }

  if (target.startsWith('/login') || target.startsWith('/password-reset')) {
    return null;
  }

  return target;
}

export function resolvePostLoginPath(user, redirectTarget, organizationId) {
  const normalizedRedirectTarget = sanitizeRedirectTarget(redirectTarget);
  const isControlPlaneHost = organizationId === 'admin';

  let destination = '/';

  if (user?.user_type === 'monitor') {
    destination = '/monitor';
  } else if (user?.user_type === 'patient') {
    destination = '/patient-portal';
  }

  if (isControlPlaneHost && user?.is_superuser) {
    return '/platform/tenants';
  }

  if (!normalizedRedirectTarget) {
    return destination;
  }

  if (user?.user_type === 'monitor') {
    return normalizedRedirectTarget.startsWith('/monitor') ? normalizedRedirectTarget : '/monitor';
  }

  if (user?.user_type === 'patient') {
    if (
      normalizedRedirectTarget.startsWith('/patient-portal') ||
      normalizedRedirectTarget.startsWith('/profile')
    ) {
      return normalizedRedirectTarget;
    }

    return '/patient-portal';
  }

  return normalizedRedirectTarget.startsWith('/platform') ? destination : normalizedRedirectTarget;
}

export function buildTenantRedirectUrl(appOrigin, pathname) {
  const normalizedPath = pathname && pathname.startsWith('/') ? pathname : '/';
  return `${trimTrailingSlash(appOrigin)}${normalizedPath}`;
}

export function clearTenantSession(cookieDomain) {
  const expiredAt = new Date(0);
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';

  TOKEN_COOKIE_NAMES.forEach((name) => {
    document.cookie = buildCookieString(name, '', { expires: expiredAt, secure });

    if (cookieDomain) {
      document.cookie = buildCookieString(name, '', {
        domain: cookieDomain,
        expires: expiredAt,
        secure,
      });
    }
  });
}

export function persistTenantSession(session, cookieDomain) {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';

  TOKEN_COOKIE_NAMES.forEach((name) => {
    const cookieValue = SESSION_COOKIE_FIELDS[name]
      .map((fieldName) => session?.[fieldName])
      .find(Boolean);

    if (!cookieValue) {
      return;
    }

    document.cookie = buildCookieString(name, encodeURIComponent(cookieValue), {
      domain: cookieDomain,
      secure,
    });
  });
}
